import React, { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { updateIntake, client, getUsersByWorkosRoles } from '../services/api';
import type { UserResponse } from '../services/api';
import { useToast } from '../hooks/useToastContext';
import { IntakeRecord } from '@/store/slices/intakeSlice';

interface IntakeUserAssignmentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intakes: IntakeRecord[];
  onSuccess?: () => void;
}

export const IntakeUserAssignmentDialog: React.FC<
  IntakeUserAssignmentDialogProps
> = ({ open, onOpenChange, intakes, onSuccess }) => {
  const intakeIds = intakes.map(intake => intake.id);
  const { error: showError } = useToast();
  const [users, setUsers] = useState<UserResponse[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isAssigning, setIsAssigning] = useState(false);

  // TODO maybe pass this to the back end at some point
  const ignoredDomains = ['rogerhealthcare.com', 'rishavanand.com'];
  const ignoredEmails = [
    'stacknine.dev@gmail.com',
    'chefking2673@gmail.com',
    'aoliver@jwohhc.com',
  ];

  // Reset selection when dialog opens; pre-populate if all intakes share the same assignee
  useEffect(() => {
    if (open) {
      const firstAssignedTo = intakes[0]?.assigned_to;
      const allSameAssignee =
        intakes.length > 0 &&
        (firstAssignedTo ?? '') !== '' &&
        intakes.every(i => (i.assigned_to ?? '') === firstAssignedTo);
      setSelectedUserId(allSameAssignee ? (firstAssignedTo as string) : '');
      if (users.length === 0) {
        loadUsers();
      }
    }
  }, [open, intakes]);

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await getUsersByWorkosRoles({
        client,
        body: ['intake'],
      });
      setUsers(
        (response.data ?? []).filter(
          user =>
            !ignoredDomains.some(domain => user.email?.includes(domain)) &&
            !ignoredEmails.some(email => user.email === email)
        )
        // Comment out above lines and uncomment below line for testing
        // response.data ?? []
      );
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleAssign = async () => {
    setIsAssigning(true);
    try {
      // Update each selected intake with the new assignee
      await Promise.all(
        intakeIds.map(intakeId =>
          updateIntake({
            path: { intake_id: intakeId },
            body: { assigned_to: selectedUserId || null },
            client,
          })
        )
      );
      onOpenChange(false);
      onSuccess?.();
    } catch {
      showError(
        `Failed to ${selectedUserId ? 'assign' : 'unassign'} intakes`,
        'Please try again or refresh the page.'
      );
    } finally {
      setIsAssigning(false);
    }
  };

  // Seed options with current assignee from intake so email shows immediately (no wait for loadUsers)
  const prefillAssignee =
    intakes.length > 0 &&
    intakes[0]?.assigned_to &&
    intakes.every(i => i.assigned_to === intakes[0].assigned_to)
      ? intakes[0].assigned_to_user
      : null;
  const prefillOption = prefillAssignee?.id
    ? {
        value: prefillAssignee.id,
        label: prefillAssignee.email ?? prefillAssignee.id,
      }
    : null;
  const usersOptions = users.map(user => ({
    value: user.id,
    label: user.email,
  }));
  const userOptions =
    prefillOption && !usersOptions.some(o => o.value === prefillOption.value)
      ? [prefillOption, ...usersOptions]
      : usersOptions;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign Intakes</DialogTitle>
          <DialogDescription>
            Select a user to assign {intakeIds.length} intake
            {intakeIds.length > 1 ? 's' : ''} to.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          <VirtualizedFormSelect
            key={open ? 'assign-user-select' : 'closed'}
            label='Select User'
            placeholder='Select a user'
            options={userOptions}
            defaultValue={selectedUserId}
            onChange={e => setSelectedUserId(e.target.value)}
            disabled={usersLoading}
            isClearable={true}
          />
        </div>

        <DialogFooter>
          <Button onClick={handleAssign} disabled={isAssigning}>
            {isAssigning
              ? selectedUserId
                ? 'Assigning...'
                : 'Unassigning...'
              : selectedUserId
                ? 'Assign'
                : 'Unassign'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
